import { classes } from 'html-classes';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import Skeleton from 'react-loading-skeleton';

import { company } from '~/company/Company';
import type { ProductViewProps } from '~/pages/Product/Product';

import styles from './ProductBadges.module.scss';

const ProductBadges = ({
  currentItem,
  isExpress,
  isBundle,
  className,
  isLoading,
}: {
  className?: string;
} & Pick<
  ProductViewProps,
  'currentItem' | 'isExpress' | 'isBundle' | 'isLoading'
>) => {
  const { t } = useTranslation();

  const isMarketplace =
    company.isMarketplaceVariant || company.useMarketplaceProductLayout;
  const items = (() => {
    const result: string[] = [];

    if (currentItem?.discountPercentage && !isMarketplace) {
      result.push(currentItem.discountPercentage);
    }

    if (isExpress && company.showExpressBadgeOnProductCards) {
      result.push(t('express'));
    }

    if (isBundle) {
      result.push(t('bundle'));
    }

    return result;
  })();

  const getClassName = (text: string) => {
    switch (text) {
      case t('express'):
        return styles.badgeBrand;
      case t('bundle'):
        return styles.badgeTransparentBrand;
      default:
        return styles.badgeRegular;
    }
  };

  const rootClassName = classes([
    styles.root,
    className,
    isMarketplace && styles.marketplace,
  ]);

  if (isLoading) {
    return (
      <div className={rootClassName}>
        <div className={styles.inner}>
          <Skeleton containerClassName={styles.skeleton} />
          <Skeleton containerClassName={styles.skeleton} />
          {!company.is('mcosmetic') && (
            <Skeleton containerClassName={styles.skeleton} />
          )}
        </div>
      </div>
    );
  }
  if (!items.length) {
    return null;
  }
  return (
    <div className={rootClassName}>
      {items.map((text) => {
        const badgeClassName = classes([
          styles.badge,
          isMarketplace && getClassName(text),
        ]);

        return (
          <span key={text} className={badgeClassName}>
            {text}
          </span>
        );
      })}
    </div>
  );
};

export default observer(ProductBadges);
